<div class="w-full flex align-items-center justify-content-center bg-center bg-cover border-bottom-1 border-primary"
    style="background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #36DEFB 100%); min-height: 20rem;">
    <div class="flex flex-column justify-content-center align-items-center text-center px-4">
        <h1 class="text-white text-5xl md:text-6xl lg:text-7xl font-bold mb-3">Free business software for all</h1>
        <ul class="list-none flex align-items-center justify-content-center p-0 m-0">
            <li><a routerLink="/" class="cursor-pointer text-white text-lg md:text-2xl font-light hover:text-primary transition-colors transition-duration-150 no-underline">Home</a></li>
            <li class="mx-2"><i class="pi pi-angle-double-right text-white text-lg md:text-2xl font-light"></i></li>
            <li><span class="text-primary text-lg md:text-2xl font-light">Free software</span></li>
        </ul>
    </div>
</div>

<!-- CONCEPT -->
<section class="bg-white py-8" data-prerender>
    <div class="mx-auto px-4 md:px-6 lg:px-8" style="max-width: 1200px;">
        <span class="text-primary-deep font-bold text-lg uppercase tracking-widest block mb-3">The idea</span>
        <h2 class="text-900 text-4xl md:text-5xl font-bold mb-5">Business software should not be a monthly bill</h2>
        <p class="text-600 text-xl line-height-4 mb-4">
            ESZES builds the BMS suite, a set of business management applications that we run as a hosted service
            for our customers. We believe every business, however small, deserves the same tools. So we are
            offering the same applications free of charge, to download and run on your own computer.
        </p>
        <p class="text-600 text-xl line-height-4 mb-4">
            The local edition keeps everything on your machine: the database, the user interface and the API. The
            only thing that touches the cloud is sign-in through our cloud identity service. Accounts and licence files
            are issued by the ESZES team on request, and there is no self-service signup.
        </p>
        <p class="text-600 text-xl line-height-4 mb-0">
            We start with Snapcart, our catalog and orders pair. More applications from the suite will follow.
        </p>
    </div>
</section>

<!-- VALUE CARDS -->
<section class="bg-gray-50 py-8">
    <div class="mx-auto px-4 md:px-6 lg:px-8" style="max-width: 1400px;">
        <div class="grid">
            <div class="col-12 md:col-4 p-3" *ngFor="let v of values">
                <div class="bg-white border-round-3xl shadow-2 p-5 h-full border-1 border-gray-100">
                    <div class="w-4rem h-4rem bg-primary-alpha-10 border-round-2xl flex align-items-center justify-content-center mb-4">
                        <i [class]="v.icon + ' text-primary text-3xl'"></i>
                    </div>
                    <h3 class="text-900 text-2xl font-bold mb-3">{{ v.title }}</h3>
                    <p class="text-600 line-height-3 text-lg m-0">{{ v.text }}</p>
                </div>
            </div>
        </div>
    </div>
</section>

<!-- FIRST PRODUCT -->
<section class="bg-gray-900 py-8 relative overflow-hidden">
    <div class="absolute top-0 left-0 w-full h-full opacity-10"
        style="background-image: radial-gradient(circle at 2px 2px, #36DEFB 1px, transparent 0); background-size: 30px 30px;"></div>
    <div class="mx-auto px-4 md:px-6 lg:px-8 relative z-1" style="max-width: 1200px;">
        <span class="text-primary font-bold text-lg uppercase tracking-widest block mb-3">First free product</span>
        <h2 class="text-white text-4xl md:text-5xl font-bold mb-4">Snapcart</h2>
        <p class="text-gray-400 text-xl line-height-4 mb-5">Three applications on your laptop: catalog and orders work as a pair, accounting runs on its own.</p>
        <div class="grid mb-5">
            <div class="col-12 md:col-4 p-3">
                <div class="bg-white-alpha-5 border-1 border-white-alpha-10 border-round-3xl p-5 h-full">
                    <h3 class="text-white text-2xl font-bold mb-2">BMS Items <span class="text-primary text-lg font-normal">catalog</span></h3>
                    <p class="text-gray-400 line-height-3 m-0">Products with variations, category tree, brands, attributes, taxes, currencies, reviews. Serves a storefront API for a shop website. Runs at <code class="text-primary">{{ links.itemsUrl }}</code></p>
                </div>
            </div>
            <div class="col-12 md:col-4 p-3">
                <div class="bg-white-alpha-5 border-1 border-white-alpha-10 border-round-3xl p-5 h-full">
                    <h3 class="text-white text-2xl font-bold mb-2">BMS Ecom <span class="text-primary text-lg font-normal">orders</span></h3>
                    <p class="text-gray-400 line-height-3 m-0">Customers, carts, orders and their status workflow, POS screen, returns and refunds, shipping and tax settings. Orders are created against the local Items catalog. Runs at <code class="text-primary">{{ links.ecomUrl }}</code></p>
                </div>
            </div>
            <div class="col-12 md:col-4 p-3">
                <div class="bg-white-alpha-5 border-1 border-white-alpha-10 border-round-3xl p-5 h-full">
                    <h3 class="text-white text-2xl font-bold mb-2">BMS Ledger <span class="text-primary text-lg font-normal">accounting</span></h3>
                    <p class="text-gray-400 line-height-3 m-0">Chart of accounts, all eight voucher kinds, stock masters with GST or VAT, day book, trial balance, profit &amp; loss, balance sheet and stock summary. Ships with starter books so the reports open with figures. Runs on localhost:8093.</p>
                </div>
            </div>
        </div>
        <div class="flex flex-wrap align-items-center gap-3">
            <button pButton label="See Snapcart" icon="pi pi-arrow-right" iconPos="right" routerLink="/snapcart"
                class="p-button-primary p-button-rounded px-6 py-3 font-bold shadow-4"></button>
            <span class="text-gray-400">Java 25 · Docker Desktop · macOS, Linux or Windows</span>
        </div>
    </div>
</section>

<!-- HOW IT WORKS -->
<section class="bg-white py-8">
    <div class="mx-auto px-4 md:px-6 lg:px-8" style="max-width: 1400px;">
        <div class="text-center mb-6">
            <span class="text-primary-deep font-bold text-lg uppercase tracking-widest block mb-3">How it works</span>
            <h2 class="text-900 text-4xl font-bold">Three steps to a running system</h2>
        </div>
        <div class="grid">
            <div class="col-12 md:col-4 p-3 text-center" *ngFor="let s of steps; let i = index">
                <div class="w-5rem h-5rem border-circle bg-gray-900 text-white flex align-items-center justify-content-center mx-auto mb-4 text-2xl font-bold">{{ i + 1 }}</div>
                <h3 class="text-900 text-2xl font-bold mb-3"><i [class]="s.icon + ' text-primary mr-2'"></i>{{ s.title }}</h3>
                <p class="text-600 line-height-3 text-lg m-0">{{ s.text }}</p>
            </div>
        </div>
    </div>
</section>

<!-- CTA -->
<section class="bg-gray-900 py-6 relative overflow-hidden">
    <div class="absolute top-0 left-0 w-full h-full opacity-20" style="background: radial-gradient(circle at center, #36DEFB 0%, transparent 70%);"></div>
    <div class="mx-auto px-4 md:px-6 lg:px-8 text-center relative z-1" style="max-width: 1000px;">
        <h2 class="text-white text-3xl md:text-5xl font-bold mb-4 mt-0 lg:mt-6">Want to try it?</h2>
        <p class="text-gray-400 text-lg mb-4 mx-auto line-height-3">Send us your machine's MAC address through the contact form. Within one business day you receive by e-mail:</p>
        <ul class="list-none p-0 m-0 mb-6 inline-block text-left text-gray-300 line-height-3">
            <li class="mb-2"><i class="pi pi-check text-primary mr-2" aria-hidden="true"></i>the licence file for that machine</li>
            <li class="mb-2"><i class="pi pi-check text-primary mr-2" aria-hidden="true"></i>a sign-in account: username and a temporary password</li>
            <li><i class="pi pi-check text-primary mr-2" aria-hidden="true"></i>the link to the kit and the installation guide</li>
        </ul>
        <div class="flex gap-3 justify-content-center flex-wrap mb-5">
            <button pButton label="Request access" icon="pi pi-envelope" iconPos="right" [routerLink]="links.requestAccess.path" [queryParams]="links.requestAccess.query"
                class="p-button-primary p-button-rounded px-6 py-3 font-bold shadow-4"></button>
            <button pButton label="Installation guide" routerLink="/snapcart"
                class="p-button-outlined p-button-secondary text-white border-white p-button-rounded px-6 py-3 font-bold"></button>
        </div>
    </div>
</section>
